import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Ruler, Image as ImageIcon, Download, Car, Warehouse } from "lucide-react";
import { FadeUp } from "./Reveal";
import { AnimatedNumber } from "./AnimatedNumber";
import { openBrochureModal } from "./BrochureModal";
import { scrollToId } from "./Header";
import { APARTMENTS, EXTRAS } from "@/data/apartments";
import { useLang } from "@/i18n";

const fmtPrice = (lang) => (n) =>
  lang === "fr" ? `${n.toLocaleString("fr-FR")} €` : lang === "en" ? `€ ${n.toLocaleString("en-GB")}` : `€ ${n.toLocaleString("it-IT")}`;

const ApartmentCard = ({ apt, index }) => {
  const { t, lang } = useLang();
  const [showPlan, setShowPlan] = useState(false);
  const info = t.apartments.items[apt.id];

  return (
    <FadeUp delay={index * 0.12} className="group" data-testid={`apartment-card-${apt.id}`}>
      <div className="relative aspect-[4/5] overflow-hidden border border-line bg-white">
        <AnimatePresence mode="wait" initial={false}>
          {showPlan ? (
            <motion.div
              key="plan"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="flex h-full w-full items-center justify-center bg-white p-4"
            >
              <img
                src={apt.planImg}
                alt={`${info.nome} — ${t.apartments.plan}`}
                loading="lazy"
                className="max-h-full max-w-full object-contain"
                data-testid={`apartment-plan-img-${apt.id}`}
              />
            </motion.div>
          ) : (
            <motion.div
              key="img"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.45 }}
              className="h-full w-full"
            >
              <img
                src={apt.img}
                alt={info.nome}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
              />
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-ink/10" />
            </motion.div>
          )}
        </AnimatePresence>
        <span className="absolute left-4 top-4 bg-ivory/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-ink backdrop-blur">
          {info.nome}
        </span>
      </div>

      <div className="mt-6 flex items-baseline justify-between gap-4">
        <h3 className="font-serif text-2xl font-medium text-ink sm:text-[1.7rem]">
          <span className="bg-gradient-to-r from-terracotta to-terracotta bg-[length:0%_1px] bg-left-bottom bg-no-repeat transition-[background-size] duration-700 group-hover:bg-[length:100%_1px]">
            {info.nome}
          </span>
        </h3>
        <button
          onClick={() => setShowPlan((v) => !v)}
          data-testid={`apartment-plan-toggle-${apt.id}`}
          className="flex shrink-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-olive transition-colors duration-300 hover:text-terracotta"
        >
          {showPlan ? <ImageIcon className="h-4 w-4" strokeWidth={1.25} /> : <Ruler className="h-4 w-4" strokeWidth={1.25} />}
          {showPlan ? t.apartments.view : t.apartments.plan}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-px border border-line bg-line">
        {[
          [t.apartments.rooms, <span key="l">{info.localiLabel || apt.locali}</span>],
          [
            t.apartments.surface,
            <span key="s">
              <AnimatedNumber value={apt.mq} /> {t.apartments.sqUnit}
            </span>,
          ],
          [t.apartments.outdoor, <span key="o">{info.esterno}</span>],
          [
            t.apartments.price,
            <span key="p">
              {apt.prezzoFrom ? `${t.apartments.from} ` : ""}
              <AnimatedNumber value={apt.prezzo} format={fmtPrice(lang)} />
            </span>,
          ],
        ].map(([k, v]) => (
          <div key={k} className="bg-white p-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#8A8D8B]">{k}</p>
            <p className={`mt-1.5 font-serif text-lg ${k === t.apartments.price ? "text-terracotta" : "text-ink"}`}>{v}</p>
          </div>
        ))}
      </div>
    </FadeUp>
  );
};

export const Apartments = () => {
  const { t, lang } = useLang();
  const EX = [
    { Icon: Car, title: t.apartments.extras.boxTitle, desc: t.apartments.extras.boxDesc, price: EXTRAS.box, testid: "extra-box" },
    { Icon: Warehouse, title: t.apartments.extras.cantinaTitle, desc: t.apartments.extras.cantinaDesc, price: EXTRAS.cantina, testid: "extra-cantina" },
  ];
  return (
    <section id="appartamenti" className="bg-bone py-28 sm:py-40" data-testid="apartments-section">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <FadeUp>
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-terracotta">{t.apartments.label}</p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 max-w-xl font-serif text-4xl font-light leading-tight tracking-tight text-ink sm:text-5xl">
                {t.apartments.h2a}
                <em className="italic text-terracotta">{t.apartments.h2em}</em>
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2} className="max-w-sm">
            <p className="text-sm leading-relaxed text-[#4A4F4C] sm:text-base">{t.apartments.desc}</p>
            <button
              onClick={openBrochureModal}
              data-testid="apartments-brochure-link"
              className="mt-5 inline-flex items-center gap-2.5 rounded-full bg-terracotta px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-ivory shadow-[0_8px_25px_rgba(194,105,12,0.35)] transition-all duration-300 hover:scale-105 hover:bg-sun hover:text-ink"
            >
              <Download className="h-4 w-4" strokeWidth={2} />
              {t.brochure}
            </button>
          </FadeUp>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-8 xl:grid-cols-4">
          {APARTMENTS.map((apt, i) => (
            <ApartmentCard key={apt.id} apt={apt} index={i} />
          ))}
        </div>

        <FadeUp delay={0.1} className="mt-20">
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A8D8B]" data-testid="extras-label">
            {t.apartments.extras.label}
          </p>
          <div className="mt-6 grid grid-cols-1 gap-px border border-line bg-line sm:grid-cols-2">
            {EX.map(({ Icon, title, desc, price, testid }) => (
              <div key={testid} className="flex items-start gap-6 bg-white p-8 sm:p-10" data-testid={testid}>
                <Icon className="mt-1 h-9 w-9 shrink-0 text-terracotta" strokeWidth={1} />
                <div>
                  <h4 className="font-serif text-2xl font-medium text-ink">{title}</h4>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-[#4A4F4C]">{desc}</p>
                  <p className="mt-4 font-serif text-2xl text-terracotta">
                    {t.apartments.from} <AnimatedNumber value={price} format={fmtPrice(lang)} />
                  </p>
                </div>
              </div>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.15} className="mt-20 text-center">
          <p className="font-serif text-3xl font-light text-ink sm:text-4xl" data-testid="apartments-cta-title">
            {t.apartments.ctaTitle}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#contatti"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#contatti");
              }}
              data-testid="apartments-cta-visit"
              className="rounded-full bg-terracotta px-10 py-4 text-xs font-bold uppercase tracking-[0.2em] text-ivory shadow-[0_10px_35px_rgba(194,105,12,0.4)] transition-all duration-300 hover:scale-[1.03] hover:bg-sun hover:text-ink"
            >
              {t.headerCta}
            </a>
            <button
              onClick={openBrochureModal}
              data-testid="apartments-cta-brochure"
              className="rounded-full border-2 border-terracotta px-10 py-4 text-xs font-bold uppercase tracking-[0.2em] text-terracotta transition-all duration-300 hover:scale-[1.03] hover:bg-terracotta hover:text-ivory"
            >
              {t.brochure}
            </button>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
